/**
 * v135 — Public sharing URL encoding.
 *
 * The share engine produces a public URL like:
 *
 *   https://sparrowexpeditions.com/pages/share?b=<encoded>
 *
 * `<encoded>` is a URL-safe base64 of the share snapshot's JSON. The /pages/share
 * route is a Shopify Page using the standard Forge template; the iframe app
 * detects the `?b=` query parameter on mount and renders the shared snapshot as
 * a clone-to-my-Forge surface.
 *
 * Why URL-encoded vs server-stored: zero server, no auth, no DB. The link is
 * the data — paste it into iMessage and the recipient can open it on any
 * device. Trade-off: max URL length ~2 KB across messaging clients, so we
 * compact field names and only include what the card renders (no internal
 * derived state).
 *
 * Reuses the existing buildPermalink format for the build variant (so a
 * v135 share link round-trips through the same code path the /tools/arrow-builder
 * permalink uses). Session + compare variants get their own slimmer formats.
 */

import { encodeBuild, decodeBuild } from '@/lib/buildPermalink';
import type {
  ShareSnapshot,
  ShareBuildSnapshot,
  ShareSessionSnapshot,
  ShareCompareSnapshot,
} from './types';

/** URL-safe base64 encoder (no padding, +/= → -_). */
function urlSafeB64(json: string): string {
  return btoa(unescape(encodeURIComponent(json)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function urlSafeB64Decode(b64: string): string {
  const padded = b64.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4 === 0 ? padded : padded + '='.repeat(4 - (padded.length % 4));
  return decodeURIComponent(escape(atob(pad)));
}

/**
 * Encode a snapshot into a URL-safe base64 payload.
 *
 * For build snapshots we delegate to encodeBuild (same format as the
 * /tools/arrow-builder permalink). For session/compare we ship a slimmer
 * compact JSON, since those don't need the full builder state.
 */
export function encodeSnapshot(snap: ShareSnapshot): { kind: string; payload: string } {
  switch (snap.variant) {
    case 'build':
      return {
        kind: 'b',
        payload: encodeBuild(snap.state),
      };
    case 'session': {
      const compact = {
        v: 1,
        d: snap.session.distanceYd,
        n: snap.session.notes?.slice(0, 200),
        c: snap.session.conditions
          ? { t: snap.session.conditions.temperatureF, w: snap.session.conditions.windMph }
          : undefined,
        a: {
          n: snap.aggregates.count,
          m: round(snap.aggregates.meanRadiusIn, 3),
          g: round(snap.aggregates.group95RadiusIn, 3),
        },
        s: snap.shots
          .slice(0, 60) // cap — most sessions are under 30 shots
          .map((p) => [round(p.xIn, 3), round(p.yIn, 3)]),
        l: snap.customLabel?.slice(0, 60),
      };
      return { kind: 's', payload: urlSafeB64(JSON.stringify(compact)) };
    }
    case 'compare': {
      const compact = {
        v: 1,
        ss: snap.sources.map((s) => ({
          l: s.label.slice(0, 40),
          c: s.color,
          n: s.count,
          m: round(s.meanRadiusIn, 3),
        })),
        a: {
          n: snap.aggregates.count,
          m: round(snap.aggregates.meanRadiusIn, 3),
          g: round(snap.aggregates.group95RadiusIn, 3),
          d: snap.aggregates.distanceYd,
        },
        s: snap.shots
          .slice(0, 90)
          .map((p) => [round(p.xIn, 3), round(p.yIn, 3), p.sourceKey ?? '']),
        l: snap.customLabel?.slice(0, 60),
      };
      return { kind: 'c', payload: urlSafeB64(JSON.stringify(compact)) };
    }
  }
}

/** Build the public share URL given an origin + snapshot. */
export function buildShareUrl(origin: string, snap: ShareSnapshot): string {
  const { kind, payload } = encodeSnapshot(snap);
  // Use the kind as the query param so the share page can pick its renderer
  // without parsing the payload first.
  return `${origin.replace(/\/$/, '')}/pages/share?k=${kind}&p=${payload}`;
}

/** Decode a share URL on the receiving side. Returns null on malformed input. */
export function decodeShareUrl(searchParams: URLSearchParams): {
  kind: 'build' | 'session' | 'compare';
  decoded: unknown;
} | null {
  const k = searchParams.get('k');
  const p = searchParams.get('p');
  if (!k || !p) return null;
  try {
    if (k === 'b') {
      const state = decodeBuild(p);
      if (!state) return null;
      return { kind: 'build', decoded: state };
    }
    if (k === 's' || k === 'c') {
      const json = urlSafeB64Decode(p);
      const parsed = JSON.parse(json);
      return { kind: k === 's' ? 'session' : 'compare', decoded: parsed };
    }
    return null;
  } catch {
    return null;
  }
}

function round(n: number, places: number): number {
  const m = 10 ** places;
  return Math.round(n * m) / m;
}

/** Convenience — extract just the encoded portion for diagnostics / tests. */
export function getShareUrlLength(origin: string, snap: ShareSnapshot): number {
  return buildShareUrl(origin, snap).length;
}

/** Maximum URL length we'll target. iMessage/Slack/Twitter handle 2KB fine;
 *  some older link previewers truncate beyond ~1500 chars. */
export const SHARE_URL_SOFT_MAX = 1800;

/** Build-variant snapshot guard — narrow inputs in the modal. */
export function isBuildSnapshot(s: ShareSnapshot): s is ShareBuildSnapshot {
  return s.variant === 'build';
}
export function isSessionSnapshot(s: ShareSnapshot): s is ShareSessionSnapshot {
  return s.variant === 'session';
}
export function isCompareSnapshot(s: ShareSnapshot): s is ShareCompareSnapshot {
  return s.variant === 'compare';
}
